/**
 * Holdings View
 * Displays holdings with classification management
 */
import { useState } from 'react';
import { useHoldings, useClassifyHolding, useDeleteHolding } from '@/hooks/useAllocation';
import { useAllocationStore, selectSelectedHoldingIds, selectFilters } from '@/store/allocationStore';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import ManageHoldingModal from './ManageHoldingModal';
import {
  ASSET_CLASS_DISPLAY_NAMES,
  type AssetClass,
  type HoldingWithClassification,
  type ConfidenceLevel,
  type ClassificationSource,
} from '@/types/allocation';

export default function HoldingsView() {
  const filters = useAllocationStore(selectFilters);
  const setAssetClassFilter = useAllocationStore((state) => state.setAssetClassFilter);
  const setNeedsReviewOnly = useAllocationStore((state) => state.setNeedsReviewOnly);
  const selectedIds = useAllocationStore(selectSelectedHoldingIds);
  const setShowBulkClassifyModal = useAllocationStore((state) => state.setShowBulkClassifyModal);
  const toggleSelection = useAllocationStore((state) => state.toggleHoldingSelection);
  const selectAll = useAllocationStore((state) => state.selectAllHoldings);
  const clearSelection = useAllocationStore((state) => state.clearHoldingSelection);

  const [page, setPage] = useState(1);
  const pageSize = 20;

  // Modal state for create/edit holding
  const [showManageModal, setShowManageModal] = useState(false);
  const [editingHoldingId, setEditingHoldingId] = useState<string | null>(null);
  // For creating holdings, we need to track which account to add to
  // This would typically come from a selected account or context
  const [createForAccountId, setCreateForAccountId] = useState<string | null>(null);

  const { data, isLoading } = useHoldings({
    page,
    pageSize,
    needsReview: filters.needsReviewOnly || undefined,
    assetClass: filters.assetClassFilter || undefined,
    includeSynthetic: true, // WP-MANUAL-HOLD-2: Include balance-derived holdings
  });

  if (isLoading || !data) {
    return <HoldingsSkeleton />;
  }

  const { holdings, count, classifiedCount, needsReviewCount = 0 } = data;

  const handleSelectAll = () => {
    if (selectedIds.length === holdings.length) {
      clearSelection();
    } else {
      selectAll(holdings.map((h) => h.holdingId));
    }
  };

  const handleEditHolding = (holdingId: string) => {
    setEditingHoldingId(holdingId);
    setCreateForAccountId(null);
    setShowManageModal(true);
  };

  const handleCloseManageModal = () => {
    setShowManageModal(false);
    setEditingHoldingId(null);
    setCreateForAccountId(null);
  };

  return (
    <div className="space-y-6">
      {/* Summary */}
      <Card>
        <div className="p-6">
          <h2 className="text-lg font-semibold text-gray-900">
            Holdings Classification
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Review and classify your investment holdings
          </p>

          <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-3">
            <div>
              <p className="text-sm font-medium text-gray-500">Total Holdings</p>
              <p className="mt-1 text-2xl font-semibold text-gray-900">
                {count}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Classified</p>
              <p className="mt-1 text-2xl font-semibold text-green-600">
                {classifiedCount}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Needs Review</p>
              <p className="mt-1 text-2xl font-semibold text-yellow-600">
                {needsReviewCount}
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Asset Class:</label>
          <select
            value={filters.assetClassFilter ?? ''}
            onChange={(e) => setAssetClassFilter(e.target.value as AssetClass || null)}
            className="rounded-md border-gray-300 text-sm focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">All</option>
            {Object.entries(ASSET_CLASS_DISPLAY_NAMES).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </div>

        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={filters.needsReviewOnly}
            onChange={(e) => setNeedsReviewOnly(e.target.checked)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-600">Needs review only</span>
        </label>

        {selectedIds.length > 0 && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => setShowBulkClassifyModal(true)}
          >
            Classify Selected ({selectedIds.length})
          </Button>
        )}
      </div>

      {/* Empty state for no holdings */}
      {count === 0 ? (
        <Card>
          <div className="p-12 text-center">
            <div className="mx-auto h-12 w-12 text-gray-400">
              <InvestmentIcon className="h-12 w-12" />
            </div>
            <h3 className="mt-4 text-lg font-medium text-gray-900">
              No holdings to classify
            </h3>
            <p className="mt-2 text-sm text-gray-500">
              Holdings classification applies to investment accounts with securities.
            </p>
            <p className="mt-1 text-sm text-gray-400">
              Connect a brokerage or retirement account to see and classify your holdings.
            </p>
          </div>
        </Card>
      ) : (
      /* Holdings table */
      <Card>
        <div className="p-6">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={holdings.length > 0 && selectedIds.length === holdings.length}
                      onChange={handleSelectAll}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Security
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Account
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Value
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Classification
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Source
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {holdings.map((holding) => (
                  <HoldingRow
                    key={holding.holdingId}
                    holding={holding}
                    isSelected={selectedIds.includes(holding.holdingId)}
                    onToggleSelect={() => toggleSelection(holding.holdingId)}
                    onEditHolding={handleEditHolding}
                  />
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {count > pageSize && (
            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm text-gray-600">
                Showing {(page - 1) * pageSize + 1} to{' '}
                {Math.min(page * pageSize, count)} of {count}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page * pageSize >= count}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      </Card>
      )}

      {/* Manage Holding Modal (create/edit) */}
      <ManageHoldingModal
        isOpen={showManageModal}
        onClose={handleCloseManageModal}
        accountId={createForAccountId ?? undefined}
        holdingId={editingHoldingId ?? undefined}
      />
    </div>
  );
}

interface HoldingRowProps {
  holding: HoldingWithClassification;
  isSelected: boolean;
  onToggleSelect: () => void;
  onEditHolding: (holdingId: string) => void;
}

function HoldingRow({ holding, isSelected, onToggleSelect, onEditHolding }: HoldingRowProps) {
  const [isClassifying, setIsClassifying] = useState(false);
  const [selectedClass, setSelectedClass] = useState(holding.assetClass);
  const [classifyError, setClassifyError] = useState<string | null>(null);
  const { mutate: classifyHolding, isPending: isClassifyPending } = useClassifyHolding();
  const { mutate: deleteHolding, isPending: isDeletePending } = useDeleteHolding();

  const handleSaveClassification = () => {
    setClassifyError(null);
    classifyHolding(
      { holdingId: holding.holdingId, data: { assetClass: selectedClass } },
      {
        onSuccess: () => setIsClassifying(false),
        onError: (err: Error & { detail?: string }) => {
          const message = err.detail || err.message || 'Failed to classify';
          setClassifyError(message);
        },
      }
    );
  };

  const handleDelete = () => {
    if (window.confirm(`Are you sure you want to delete "${holding.securityName}"? This action cannot be undone.`)) {
      deleteHolding(holding.holdingId);
    }
  };

  // WP-MANUAL-HOLD-2: Determine holding source for badges and action availability
  const isSynthetic = holding.isSynthetic === true;
  const isManual = holding.isManual === true && !isSynthetic;
  // Can only edit/delete manual holdings; synthetic and synced holdings are immutable
  const canEditOrDelete = isManual;

  return (
    <tr className={`hover:bg-gray-50 ${holding.needsReview ? 'bg-yellow-50' : ''}`}>
      <td className="px-4 py-4">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onToggleSelect}
          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
      </td>
      <td className="px-4 py-4">
        <div>
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-gray-900">{holding.securityName}</p>
            {/* WP-MANUAL-HOLD-2: Holding source badge (Synced/Manual/Balance-derived) */}
            <HoldingSourceBadge
              isSynthetic={isSynthetic}
              isManual={isManual}
            />
          </div>
          {holding.ticker && (
            <p className="text-xs text-gray-500">{holding.ticker}</p>
          )}
        </div>
      </td>
      <td className="px-4 py-4">
        <p className="text-sm text-gray-600">{holding.accountName}</p>
      </td>
      <td className="px-4 py-4 text-right">
        <span className="text-sm font-medium text-gray-900">
          ${formatNumber(holding.currentValue)}
        </span>
      </td>
      <td className="px-4 py-4 text-center">
        {isClassifying ? (
          <div className="space-y-1">
            <select
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value as AssetClass)}
              className="rounded-md border-gray-300 text-sm focus:ring-blue-500 focus:border-blue-500"
            >
              {Object.entries(ASSET_CLASS_DISPLAY_NAMES).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
            {classifyError && (
              <p className="text-xs text-red-600 max-w-xs text-left">{classifyError}</p>
            )}
          </div>
        ) : (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
            {ASSET_CLASS_DISPLAY_NAMES[holding.assetClass]}
          </span>
        )}
      </td>
      <td className="px-4 py-4 text-center">
        <SourceBadge source={holding.classificationSource} confidence={holding.confidence} />
      </td>
      <td className="px-4 py-4 text-right">
        {isClassifying ? (
          <div className="flex items-center justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => { setIsClassifying(false); setClassifyError(null); }}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleSaveClassification}
              isLoading={isClassifyPending}
            >
              Save
            </Button>
          </div>
        ) : (
          <div className="flex items-center justify-end gap-1">
            <Button variant="ghost" size="sm" onClick={() => setIsClassifying(true)}>
              Classify
            </Button>
            {/* WP-MANUAL-HOLD-2: Only manual holdings can be edited/deleted */}
            {canEditOrDelete && (
              <>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onEditHolding(holding.holdingId)}
                >
                  Edit
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleDelete}
                  isLoading={isDeletePending}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  Delete
                </Button>
              </>
            )}
          </div>
        )}
      </td>
    </tr>
  );
}

interface SourceBadgeProps {
  source: ClassificationSource;
  confidence: ConfidenceLevel;
}

function SourceBadge({ source, confidence }: SourceBadgeProps) {
  const sourceLabels: Record<ClassificationSource, string> = {
    user_override: 'Manual',
    provider_data: 'Provider',
    ticker_lookup: 'Ticker',
    security_type: 'Security Type',
    default: 'Default',
  };

  const confidenceColors: Record<ConfidenceLevel, string> = {
    high: 'bg-green-100 text-green-800',
    medium: 'bg-yellow-100 text-yellow-800',
    low: 'bg-red-100 text-red-800',
  };

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${confidenceColors[confidence]}`}>
      {sourceLabels[source]}
    </span>
  );
}

/**
 * WP-MANUAL-HOLD-2: Badge showing holding source (Synced/Manual/Balance-derived)
 */
interface HoldingSourceBadgeProps {
  isSynthetic: boolean;
  isManual: boolean;
}

function HoldingSourceBadge({ isSynthetic, isManual }: HoldingSourceBadgeProps) {
  if (isSynthetic) {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700">
        Balance-derived
      </span>
    );
  }
  if (isManual) {
    return (
      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
        Manual
      </span>
    );
  }
  return (
    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
      Synced
    </span>
  );
}

function InvestmentIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={1.5}
      stroke="currentColor"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28m5.94 2.28l-2.28 5.941"
      />
    </svg>
  );
}

function HoldingsSkeleton() {
  return (
    <div className="space-y-6">
      <Card>
        <div className="p-6 animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/4 mb-4" />
          <div className="grid grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-12 bg-gray-200 rounded" />
            ))}
          </div>
        </div>
      </Card>
      <Card>
        <div className="p-6 animate-pulse">
          <div className="space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="h-12 bg-gray-200 rounded" />
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
}

function formatNumber(value: string | number | null | undefined): string {
  if (value == null || value === '') return '0';
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (Number.isNaN(num)) return '0';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(num);
}
